import { Button, Icon, Text, useToast } from '@chakra-ui/react';
import palette from '@theme/assets/palette';
import { VAT_VALUE_EXCEPTION } from '@utils/constants';
import { validateTaxIdCode, validateVat } from '@utils/functions';
import { FC, RefObject, useCallback, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { MdCheckCircle } from 'react-icons/md';
import { FieldsChecked } from 'types/formData';

interface Props {
    vat: string | null;
    vatGroup: string | null;
    taxIdCode: string | null;
    specialCustomsArrangements: boolean;
    country: string;
    status: boolean | null;
    onValidate: (fieldsChecked: FieldsChecked) => void;
    recaptchaRef: RefObject<ReCAPTCHA>;
    requiredTaxIdCode: boolean;
}

const VatManualValidation: FC<Props> = ({
    vat,
    vatGroup,
    taxIdCode,
    specialCustomsArrangements,
    country,
    status,
    onValidate,
    recaptchaRef,
    requiredTaxIdCode,
}) => {
    const [isValidWithException, setIsValidWithException] = useState(false);

    const toast = useToast();

    const checkVatValidation = useCallback(async () => {
        if (recaptchaRef && recaptchaRef.current) {
            setIsValidWithException(false);
            const fieldValidations: FieldsChecked = {
                vat: !vat ? null : specialCustomsArrangements ? true : false,
                vatGroup: !vatGroup ? null : specialCustomsArrangements ? true : false,
                taxIdCode: requiredTaxIdCode && !taxIdCode ? 'Campo obbligatorio' : taxIdCode ? false : null,
            };
            if (vat && !fieldValidations.vat) {
                const validVat = await validateVat(vat, country, recaptchaRef.current);
                if (validVat === true || validVat === VAT_VALUE_EXCEPTION) {
                    if (validVat === VAT_VALUE_EXCEPTION) {
                        toast({
                            title: 'AVVISO',
                            description:
                                'La validazione non è andata a buon fine. Si prega di procedere con una verifica manuale',
                            status: 'warning',
                            duration: 10000,
                            isClosable: true,
                            position: 'top',
                            colorScheme: 'yellow',
                        });
                        setIsValidWithException(true);
                    }

                    fieldValidations.vat = true;
                } else {
                    fieldValidations.vat = validVat;
                }
            }
            if (vatGroup && !fieldValidations.vatGroup) {
                const validVatGroup = await validateVat(vatGroup, country, recaptchaRef.current);
                if (validVatGroup === true || validVatGroup === VAT_VALUE_EXCEPTION) {
                    if (validVatGroup === VAT_VALUE_EXCEPTION) {
                        toast({
                            title: 'AVVISO',
                            description:
                                'La validazione non è andata a buon fine. Si prega di procedere con una verifica manuale',
                            status: 'warning',
                            duration: 10000,
                            isClosable: true,
                            position: 'top',
                        });
                        setIsValidWithException(true);
                    }

                    fieldValidations.vatGroup = true;
                } else {
                    fieldValidations.vat = validVatGroup;
                }
            }
            if (taxIdCode && !fieldValidations.taxIdCode) {
                const validTaxIdCode = await validateTaxIdCode(
                    taxIdCode,
                    country,
                    recaptchaRef.current,
                    specialCustomsArrangements,
                    requiredTaxIdCode,
                );
                if (validTaxIdCode === true) {
                    fieldValidations.taxIdCode = true;
                } else {
                    fieldValidations.taxIdCode = validTaxIdCode;
                }
            }
            onValidate(fieldValidations);
        }
    }, [
        country,
        onValidate,
        recaptchaRef,
        requiredTaxIdCode,
        specialCustomsArrangements,
        taxIdCode,
        toast,
        vat,
        vatGroup,
    ]);

    return (
        <>
            <Button
                rightIcon={<Icon as={MdCheckCircle} />}
                size="lg"
                variant={'main'}
                bg={palette.brand01}
                type="button"
                onClick={checkVatValidation}
                isDisabled={(!vat && !vatGroup && !taxIdCode) || status === true || !country}
            >
                {country === 'it' ? 'Convalida P. IVA/C.F.' : 'Valida P.IVA'}
            </Button>
            {status === true && !isValidWithException && (
                <Text color="green" textAlign="center" fontSize="14px">
                    {country === 'it' ? 'P. IVA/C.F. validi' : 'P.IVA valida'}
                </Text>
            )}
            {status === false && (
                <Text color="red" textAlign="center" fontSize="14px">
                    {country === 'it' ? 'P. IVA/C.F. non validi' : 'P.IVA non valida'}
                </Text>
            )}
        </>
    );
};

export default VatManualValidation;
